import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronFirst, ChevronLast, Plus, Trash2, X } from "lucide-react";
import {
  STROKE_SIDES,
  STROKE_SPINS,
  firstPhaseMs,
  loadStrokeLabels,
  newStrokeId,
  normalizePhase,
  phaseDurationMs,
  phasesForSide,
  saveStrokeLabels,
  strokeDurationMs,
  strokePhaseCount,
  tagStrokePhase,
} from "../utils/strokeLabelModel";
import "./StrokeLabelEditor.css";

function formatClock(ms) {
  const total = Math.max(0, Math.round(Number(ms) || 0));
  const sec = Math.floor(total / 1000);
  const frac = Math.floor((total % 1000) / 10);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}.${String(frac).padStart(2, "0")}`;
}

function formatDuration(ms) {
  const total = Math.max(0, Math.round(Number(ms) || 0));
  if (total < 1000) return `${total} ms`;
  const sec = total / 1000;
  return `${sec.toFixed(2)} s`;
}

const SIDE_LABEL = {
  forehand: "Forehand",
  backhand: "Backhand",
  serve: "Serve",
};

const SPIN_LABEL = {
  topspin: "Topspin",
  drive: "Drive",
  block: "Block",
  push: "Push",
  smash: "Smash",
  chop: "Chop",
  lob: "Lob",
  serve: "Serve",
};

const PHASE_LABEL = {
  preparation: "Preparation",
  forward_swing: "Forward swing",
  contact: "Contact",
  follow_through: "Follow-through",
  recovery: "Recovery",
  toss: "Toss",
  swing: "Swing",
};

/**
 * Compact sheet to stamp the paused frame as a stroke phase.
 * Labels stay on this device, keyed by the clip.
 */
export function StrokeLabelEditor({
  open,
  onClose,
  storageKey,
  currentTimeSec = 0,
  onSeek,
  onStepFrame,
  t,
}) {
  const [strokes, setStrokes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [side, setSide] = useState("forehand");
  const [spin, setSpin] = useState("topspin");
  const [selectedPhase, setSelectedPhase] = useState("preparation");

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onClose?.();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const loaded = loadStrokeLabels(storageKey);
    setStrokes(loaded);
    setActiveId(loaded[loaded.length - 1]?.id || null);
    const last = loaded[loaded.length - 1];
    if (last?.side) setSide(last.side);
    if (last?.spin && last.spin !== "serve") setSpin(last.spin);
    return undefined;
  }, [open, storageKey]);

  const active = useMemo(
    () => strokes.find((stroke) => stroke.id === activeId) || null,
    [strokes, activeId]
  );
  const phases = phasesForSide(side);
  const phaseKey = phases.includes(selectedPhase) ? selectedPhase : phases[0];
  const tMs = Math.round(Math.max(0, Number(currentTimeSec) || 0) * 1000);
  const strokeDuration = strokeDurationMs(active);

  const persist = (next, nextActive) => {
    setStrokes(next);
    setActiveId(nextActive);
    saveStrokeLabels(storageKey, next);
  };

  const stamp = (edge) => {
    const result = tagStrokePhase(strokes, {
      strokeId: activeId || newStrokeId(),
      phase: phaseKey,
      tMs,
      side,
      spin,
      edge,
    });
    persist(result.strokes, result.activeId);
  };

  const startNew = () => {
    const id = newStrokeId();
    const stroke = {
      id,
      side,
      spin: side === "serve" ? "serve" : spin,
      phases: {},
    };
    persist([...strokes, stroke], id);
  };

  const removeActive = () => {
    const next = strokes.filter((stroke) => stroke.id !== activeId);
    persist(next, next[next.length - 1]?.id || null);
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="stroke-label-editor" role="presentation">
      <button
        type="button"
        className="stroke-label-editor__backdrop"
        aria-label={t("close", "Close")}
        onClick={onClose}
      />
      <section
        className="stroke-label-editor__sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t("labelStroke", "Label stroke")}
      >
        <header className="stroke-label-editor__head">
          <div>
            <p className="stroke-label-editor__kicker">
              {t("labelStroke", "Label stroke")}
            </p>
            <p className="stroke-label-editor__time">
              {formatClock(tMs)}
              {strokeDuration != null ? (
                <span className="stroke-label-editor__stroke-duration">
                  {t("strokeDuration", "Stroke")} {formatDuration(strokeDuration)}
                </span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            className="stroke-label-editor__icon-btn"
            onClick={onClose}
            aria-label={t("close", "Close")}
          >
            <X size={18} />
          </button>
        </header>

        <div className="stroke-label-editor__row" role="group" aria-label={t("strokeSide", "Stroke side")}>
          {STROKE_SIDES.map((item) => (
            <button
              key={item}
              type="button"
              className={`stroke-label-editor__chip ${
                side === item ? "stroke-label-editor__chip--on" : ""
              }`}
              onClick={() => setSide(item)}
            >
              {t(`strokeSide_${item}`, SIDE_LABEL[item])}
            </button>
          ))}
        </div>

        {side !== "serve" ? (
          <div className="stroke-label-editor__row" role="group" aria-label={t("strokeSpin", "Stroke type")}>
            {STROKE_SPINS.map((item) => (
              <button
                key={item}
                type="button"
                className={`stroke-label-editor__chip ${
                  spin === item ? "stroke-label-editor__chip--on" : ""
                }`}
                onClick={() => setSpin(item)}
              >
                {t(`strokeSpin_${item}`, SPIN_LABEL[item])}
              </button>
            ))}
          </div>
        ) : null}

        <p className="stroke-label-editor__hint">
          {t(
            "labelStrokeRangeHint",
            "Choose a phase, step to a frame, then mark In and Out."
          )}
        </p>

        <div className="stroke-label-editor__phases">
          {phases.map((phase) => {
            const range = normalizePhase(active?.phases?.[phase]);
            const duration = phaseDurationMs(range);
            const hasStart = Number.isFinite(range?.t_start_ms);
            const hasEnd = Number.isFinite(range?.t_end_ms);
            return (
              <button
                key={phase}
                type="button"
                className={`stroke-label-editor__phase ${
                  phase === phaseKey ? "stroke-label-editor__phase--on" : ""
                } ${hasStart || hasEnd ? "stroke-label-editor__phase--set" : ""}`}
                onClick={() => setSelectedPhase(phase)}
                aria-pressed={phase === phaseKey}
              >
                <span>{t(`strokePhase_${phase}`, PHASE_LABEL[phase])}</span>
                <span className="stroke-label-editor__phase-time">
                  {hasStart || hasEnd
                    ? `${hasStart ? formatClock(range.t_start_ms) : "—"}–${
                        hasEnd ? formatClock(range.t_end_ms) : "—"
                      }`
                    : t("tagRange", "In – Out")}
                </span>
                <span className="stroke-label-editor__phase-duration">
                  {duration != null ? formatDuration(duration) : "—"}
                </span>
              </button>
            );
          })}
        </div>

        <div className="stroke-label-editor__actions">
          <button type="button" onClick={() => onStepFrame?.(false)} aria-label={t("previousFrame", "Previous frame")}>
            <ChevronFirst size={18} />
            <span>1f</span>
          </button>
          <div className="stroke-label-editor__stamper" role="group" aria-label={t("frameRangeStamper", "Frame range")}>
            <button type="button" onClick={() => stamp("start")}>
              {t("markIn", "In")}
            </button>
            <span className="stroke-label-editor__stamper-time">{formatClock(tMs)}</span>
            <button type="button" onClick={() => stamp("end")}>
              {t("markOut", "Out")}
            </button>
          </div>
          <button type="button" onClick={() => onStepFrame?.(true)} aria-label={t("nextFrame", "Next frame")}>
            <ChevronLast size={18} />
            <span>1f</span>
          </button>
          <button type="button" onClick={startNew}>
            <Plus size={16} />
            <span>{t("newStroke", "New stroke")}</span>
          </button>
          <button type="button" onClick={removeActive} disabled={!activeId}>
            <Trash2 size={16} />
            <span>{t("delete", "Delete")}</span>
          </button>
        </div>

        {strokes.length ? (
          <ul className="stroke-label-editor__list">
            {strokes.map((stroke, index) => {
              const start = firstPhaseMs(stroke);
              const duration = strokeDurationMs(stroke);
              return (
                <li key={stroke.id}>
                  <button
                    type="button"
                    className={
                      stroke.id === activeId
                        ? "stroke-label-editor__list-btn stroke-label-editor__list-btn--on"
                        : "stroke-label-editor__list-btn"
                    }
                    onClick={() => {
                      setActiveId(stroke.id);
                      if (stroke.side) setSide(stroke.side);
                      if (stroke.spin && stroke.spin !== "serve") setSpin(stroke.spin);
                      if (start != null) onSeek?.(start / 1000);
                    }}
                  >
                    <span>
                      {index + 1}. {t(`strokeSide_${stroke.side}`, SIDE_LABEL[stroke.side] || stroke.side)}{" "}
                      {stroke.side === "serve"
                        ? ""
                        : t(`strokeSpin_${stroke.spin}`, SPIN_LABEL[stroke.spin] || "")}
                    </span>
                    <span>
                      {duration != null ? formatDuration(duration) : "—"} ·{" "}
                      {strokePhaseCount(stroke)}/{phasesForSide(stroke.side).length}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </div>,
    document.body
  );
}
